import {useCallback, useMemo} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  AGENT_SKILLS_QUERY,
  AgentSkillsQueryData,
  archiveSkill as archiveSkillApi,
  bulkCreateSkills,
  SkillSummary,
} from 'llm/chat-library/skills/SkillsDAO';
import EntityStore from 'stores/EntityStore';
import * as ImportUtil from 'util/ImportUtil';

interface UseSkillsResult {
  skills: Array<SkillSummary>;
  isLoading: boolean;
  archiveSkill: (skillId: string, reason: string) => Promise<void>;
  createNewSkills: (files: Array<File>) => Promise<void>;
  refresh: () => Promise<void>;
}

export default function useSkills({enabled = true}: {enabled?: boolean} = {}): UseSkillsResult {
  const queryResult = useSkippableBenchlingQuery<AgentSkillsQueryData>(AGENT_SKILLS_QUERY, {
    skip: !enabled,
    fetchPolicy: 'network-only',
  });

  const currentUserId = EntityStore.currentUser()?.id ?? null;

  const skills: Array<SkillSummary> = useMemo(() => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return [];
    }
    return queryResult.data.agentSkills.map((skill) => ({
      id: skill.id,
      name: skill.name,
      description: skill.description,
      createdAt: skill.createdAt,
      isOwnedByCurrentUser: currentUserId != null && skill.creatorId === currentUserId,
      creatorName: skill.creatorName ?? undefined,
      extensionDefinitionId: skill.extensionDefinitionId,
    }));
  }, [queryResult, currentUserId]);

  const isLoading = !enabled ? false : queryResult.type === QueryResultType.PENDING;

  const archiveSkillHandler = useCallback(
    async (skillId: string, reason: string) => {
      await archiveSkillApi(skillId, reason);
      if (queryResult.type === QueryResultType.SUCCESS) {
        await queryResult.refetch();
      }
    },
    [queryResult]
  );

  const createNewSkills = useCallback(
    async (files: Array<File>) => {
      const s3Locations = await Promise.all(
        files.map(async (file) => {
          const location = await ImportUtil.upload(file);
          if (location == null) {
            throw new Error(`Failed to upload file: ${file.name}`);
          }
          return location;
        })
      );
      await bulkCreateSkills(s3Locations);
      if (queryResult.type === QueryResultType.SUCCESS) {
        await queryResult.refetch();
      }
    },
    [queryResult]
  );

  const refresh = useCallback(async () => {
    if (queryResult.type === QueryResultType.SUCCESS) {
      await queryResult.refetch();
    }
  }, [queryResult]);

  return {
    skills,
    isLoading,
    archiveSkill: archiveSkillHandler,
    createNewSkills,
    refresh,
  };
}
